import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { memo, useEffect, useMemo } from 'react';
import * as classnames from 'classnames';
import { prepare_description } from 'console//tabs/legacy/opentherm-dialog/opentherm-errcodes-legacy';
import { OpenThermFaultFlag, OpenThermStatusFlag } from 'openTherm';
import { pad0 } from 'utils';
import { MessageBox } from 'shared/components/dialogs/message-box';
import { loadOTErrcode } from 'console/redux/opentherm-errcodes/actions';
import { preventDefault, useCallbacks } from 'shared/hooks/utils';
import './boiler-fail-pane.sass';
import { select_opentherm_error_description } from 'console/redux/opentherm-errcodes/reducer';
import { useAction, useAppSelector } from 'shared/hooks/redux/hooks';
import { BoilerAdapterProtocol } from 'z3k-config';
import { useBooleanState } from 'shared/hooks/state/boolean';
function OTErrorDescriptionLink(props) {
    const { spec, onOpenErrorDialog } = props;
    const prepared = prepare_description(spec);
    if (prepared == null) {
        return null;
    }
    const { title, more_title } = prepared;
    const onClick = useCallbacks([preventDefault, onOpenErrorDialog]);
    if (title != null) {
        if (more_title != null)
            return (_jsxs("span", { children: [title, _jsx("br", {}), "(", _jsx("a", { href: "#", onClick: onClick, children: "\u0432\u043E\u0437\u043C\u043E\u0436\u043D\u044B\u0435 \u043F\u0440\u0438\u0447\u0438\u043D\u044B" }), ")"] }));
        return _jsx("span", { children: title });
    }
    return (_jsx("a", { href: "#", onClick: onClick, children: "\u0412\u0430\u0440\u0438\u0430\u043D\u0442\u044B \u0440\u0430\u0441\u0448\u0438\u0444\u0440\u043E\u0432\u043A\u0438" }));
}
function OTErrorDialog(props) {
    const { open, spec, onClose } = props;
    const prepared = prepare_description(spec);
    if (prepared == null) {
        return null;
    }
    const { more_title, more_html } = prepared;
    if (more_html == null)
        return null;
    if (more_title)
        return (_jsx(MessageBox, { open: open, title: more_title, isError: true, onClose: onClose, children: _jsx("div", { className: "ui-boiler-fail-pane-dialog", dangerouslySetInnerHTML: { __html: more_html } }) }));
    return null;
}
const faultFlagTitles = [
    [OpenThermFaultFlag.sr, 'Необходимо обслуживание'],
    [OpenThermFaultFlag.lr, 'Требуется ручной сброс'],
    [OpenThermFaultFlag.wp, 'Низкое давление теплоносителя'],
    [OpenThermFaultFlag.gf, 'Сбой газа/горелки'],
    [OpenThermFaultFlag.ap, 'Сбой давления воздуха'],
    [OpenThermFaultFlag.wot, 'Перегрев теплоносителя'],
];
export const OTErrorBlock = memo(function OTErrorBlock(props) {
    const [errDialogOpen, closeErrDialog, openErrDialog] = useBooleanState(false);
    // const ot_errcodes_cache = useAppSelector(
    //     (state: AppState) => state.ot_errcodes_cache
    // );
    const { ot, ot_boiler_type, title, ba_protocol } = props;
    const ff = ot.ff;
    const errorLoadingState = useAppSelector((state) => ot_boiler_type != null
        ? select_opentherm_error_description(state, ot_boiler_type, ot)
        : null);
    const loadDescription = useAction(loadOTErrcode);
    useEffect(() => {
        if (ot_boiler_type != null && errorLoadingState == null)
            loadDescription(ot_boiler_type, ba_protocol, ot);
    }, [ot_boiler_type, ot, loadDescription]);
    const oem_code = errorLoadingState?.result?.display_as != null
        ? `Код ошибки ${errorLoadingState.result.display_as}`
        : ff?.c != null
            ? `Код ошибки E${pad0(ff.c, 2)}`
            : null;
    const ot_error_flags = useMemo(() => faultFlagTitles
        .filter(([flag]) => ff?.f.includes(flag))
        .map(([, title]) => title), [ff?.f]);
    return (_jsxs("div", { className: "ot-error-block", children: [title != null && _jsx("h2", { children: title }), oem_code && _jsx("p", { children: oem_code }), ot_boiler_type != null &&
                ff?.c != null &&
                (errorLoadingState?.loading ? (_jsx("p", { style: { fontStyle: 'italic' }, children: "\u0437\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u043E\u043F\u0438\u0441\u0430\u043D\u0438\u044F \u043E\u0448\u0438\u0431\u043A\u0438..." })) : errorLoadingState?.result != null ? (_jsxs("p", { children: [_jsx(OTErrorDescriptionLink, { spec: errorLoadingState.result, onOpenErrorDialog: openErrDialog }), _jsx(OTErrorDialog, { open: errDialogOpen, spec: errorLoadingState.result, onClose: closeErrDialog })] })) : null), ot_error_flags.map((title, i) => (_jsx("p", { children: title }, i)))] }));
});
export function ot_has_error(boiler_type, ot) {
    if (boiler_type == 'dedietrich-naneo-s') {
        const err_in_s = (ot?.s ?? []).includes(OpenThermStatusFlag.f);
        const err_in_ff = (ot?.ff?.f ?? []).includes(OpenThermFaultFlag.sr);
        const err_in_rbp = ot?.rbp != null &&
            !(ot.rbp[0] == 255 && ot.rbp[1] == 255) &&
            !(ot.rbp[0] == 0 && ot.rbp[1] == 0);
        return err_in_s || err_in_ff || err_in_rbp;
    }
    return (ot?.s ?? []).includes(OpenThermStatusFlag.f);
}
export const BoilerFailPane = memo(function BoilerFailPane(props) {
    const { fail, single_ot, multi_ot, ot_zones_count = 0, ot_boiler_type } = props;
    let show;
    if (fail) {
        show = true;
    }
    else if (ot_has_error(ot_boiler_type, single_ot)) {
        show = true;
    }
    else {
        show = Object.entries(multi_ot ?? {}).some(([zone, ot]) => +zone < ot_zones_count && ot_has_error(ot_boiler_type, ot));
    }
    if (!show) {
        return null;
    }
    return (_jsxs("div", { className: classnames('ui-card', 'ui-thermostat-ext-modes-tab-fail-pane'), children: [_jsx("h1", { children: "\u0410\u0412\u0410\u0420\u0418\u042F \u041A\u041E\u0422\u041B\u0410" }), single_ot != null && ot_has_error(ot_boiler_type, single_ot) && (_jsx(OTErrorBlock, { ot: single_ot, ot_boiler_type: ot_boiler_type, 
                //legacy devices support only opentherm
                ba_protocol: BoilerAdapterProtocol.OPENTHERM })), multi_ot != null &&
                Object.entries(multi_ot ?? {})
                    .filter((item) => {
                    const [zone, ot] = item;
                    return (+zone < ot_zones_count && ot_has_error(ot_boiler_type, ot));
                })
                    .map(([zone, ot]) => (_jsx(OTErrorBlock, { ot: ot, ot_boiler_type: ot_boiler_type, title: ot_zones_count > 1 ? `Адаптер №${+zone + 1}` : undefined, 
                    //legacy devices support only opentherm
                    ba_protocol: BoilerAdapterProtocol.OPENTHERM }, zone)))] }));
});
